// Scheduler types and constants for the scheduler component and utilities.
// All types and constants are centralized here for better organization.

import { colors, spacing } from "@minuthq/meatball-ui-react"

import { TTimezone } from "src/data/timezones/timezones"
import { TClockType } from "src/data/user/user"
import { ZonedDateTime } from "src/utils/ZonedDateTime"

export type TSchedulerViewType = "week" | "month" | "year"
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type TSchedulerEvent = {
  id: string
  title: string
  start: ZonedDateTime
  end: ZonedDateTime
  resourceId: string
  color?: string
  fillColor?: string
  borderColor?: string
  priority?: number
}

export type TSchedulerResource = {
  id: string
  name: string
}

export type TSchedulerDividerStyle = {
  show?: boolean
  color?: string
  width?: number
}

export type TCurrentPeriodHighlight = {
  show?: boolean
  backgroundColor?: string
  textColor?: string
  borderRadius?: number
}

export type THeaderDateCellProps = {
  date: ZonedDateTime
  viewType: TSchedulerViewType
  isCurrentPeriod: boolean
  highlightCurrentPeriod?: TCurrentPeriodHighlight
  renderHeaderDateLabel?: (date: ZonedDateTime) => React.ReactNode
  onDateClick?: (date: ZonedDateTime) => void
  currentPeriodBackground?: string
  currentPeriodBorderColor?: string
  currentPeriodTextColor?: string
  currentPeriodBorderRadius?: number
}

type THeaderDateStyles = {
  currentPeriodBackground?: string
  currentPeriodBorderColor?: string
  currentPeriodTextColor?: string
  currentPeriodBorderRadius?: number
}

export type TSchedulerHeaderOptions = {
  resourceTitle?: string
  renderDateLabel?: (date: ZonedDateTime) => React.ReactNode
  onDateClick?: (date: ZonedDateTime) => void
  columnDividers?: TSchedulerDividerStyle
  headerDateStyles?: THeaderDateStyles
}

export type TSchedulerGridOptions = {
  verticalLines?: TSchedulerDividerStyle
  horizontalLines?: TSchedulerDividerStyle
  highlightCurrentPeriod?: TCurrentPeriodHighlight
}

export type TSchedulerProps = {
  events: TSchedulerEvent[]
  resources?: TSchedulerResource[]
  startDate: ZonedDateTime
  endDate: ZonedDateTime
  onEventClick?: (event: TSchedulerEvent) => void
  renderEvent?: (event: TSchedulerEvent) => React.ReactNode
  renderResourceLabel?: (resource: TSchedulerResource) => React.ReactNode
  headerOptions?: TSchedulerHeaderOptions
  gridOptions?: TSchedulerGridOptions
  style?: React.CSSProperties
  clockType?: TClockType
  timezone: TTimezone["tzCode"]
  viewType?: TSchedulerViewType
  maxLanes?: number
  height?: number
  moreIndicatorText?: (count: number) => string
}

export type TEventSpanInfo = {
  spanWidth: number
  isSpanStart: boolean
  isSpanEnd: boolean
  spanStartIndex: number
  spanEndIndex: number
}

export type TEventWithSpanInfo = {
  event: TSchedulerEvent
  spanInfo: TEventSpanInfo
}

export type TEventWithLane = TEventWithSpanInfo & {
  lane: number
}

export type TTooltipPosition = {
  left: number
  top: number
  transform: string
}

export type TTooltipPositionParams = {
  mouseX: number
  mouseY: number
  tooltipWidth?: number
  tooltipHeight?: number
  offset?: number
}

// Constants
export const DEFAULT_EVENT_COLORS = {
  BACKGROUND: colors.primary,
  TEXT: colors.textContrast,
} as const

export const SCHEDULER_DIMENSIONS = {
  MIN_WIDTH: 100,
  STICKY_LABEL_WIDTH: 150,
  LANE_HEIGHT: 25,
  LANE_TOP_MARGIN: 8,
  CELL_BOTTOM_PADDING: 8,
  MIN_CELL_HEIGHT: 40,
  EVENT_HEIGHT: 22,
  EVENT_PADDING: `${spacing.XS3} ${spacing.XS}`,
  EVENT_LEFT_MARGIN: 2,
  EVENT_WIDTH_MARGIN: 4,
} as const

export const TOOLTIP_DEFAULTS = {
  WIDTH: 250,
  HEIGHT: 150,
  OFFSET: 8,
  Y_OFFSET: -8,
} as const

export const SCHEDULER_DATE_FORMATS = {
  WEEK_DAY: "EEE",
  DAY_MONTH: "d MMM",
  MONTH: "MMM",
  YEAR: "yyyy",
  MONTH_YEAR: "MMMM yyyy",
} as const
